import { useLayoutEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import type { PolicyLink } from '../services/types'
import { decodePolicyRef } from '../utils/decodePolicyRef'
import {
  buildClaimOtherFireDriverHref,
  buildClaimOtherMultiVehicleDriverHref,
  buildClaimOtherSingleVehicleDriverHref,
  buildClaimOtherTheftDriverHref,
  buildClaimOtherTowingHref,
  buildClaimOtherWeatherDriverHref,
} from '../claims/claimFlowModel'
import { parseOtherClaimSubtypeQuery } from '../claims/otherClaimSubtypeOptions'

/**
 * Legacy `/claims/other/gst` route: GST / ITC is collected on the windscreen review step only.
 * Redirects off-road to towing and other subtypes to their driver step for backwards compatibility.
 */
export default function ClaimOtherGstStep() {
  const navigate = useNavigate()
  const location = useLocation()
  const queryParams = new URLSearchParams(location.search)
  const policyRef = decodePolicyRef(queryParams.get('policyRef'))
  const typeInQuery = queryParams.get('type')
  const claimSubtype = parseOtherClaimSubtypeQuery(queryParams.get('claimSubtype'))
  const navState = location.state as { policy?: PolicyLink; preserveScroll?: boolean } | null

  useLayoutEffect(() => {
    const navOpts = {
      replace: true as const,
      state: navState?.policy ? { policy: navState.policy } : undefined,
    }

    if (claimSubtype === 'multi-vehicle-collision') {
      navigate(buildClaimOtherMultiVehicleDriverHref(policyRef, typeInQuery, claimSubtype), navOpts)
      return
    }
    if (claimSubtype === 'weather') {
      navigate(buildClaimOtherWeatherDriverHref(policyRef, typeInQuery, claimSubtype), navOpts)
      return
    }
    if (claimSubtype === 'fire') {
      navigate(buildClaimOtherFireDriverHref(policyRef, typeInQuery, claimSubtype), navOpts)
      return
    }
    if (claimSubtype === 'theft') {
      navigate(buildClaimOtherTheftDriverHref(policyRef, typeInQuery, claimSubtype), navOpts)
      return
    }
    if (claimSubtype === 'single-vehicle') {
      navigate(buildClaimOtherSingleVehicleDriverHref(policyRef, typeInQuery, claimSubtype), navOpts)
      return
    }

    navigate(buildClaimOtherTowingHref(policyRef, typeInQuery, claimSubtype), navOpts)
  }, [claimSubtype, navigate, navState?.policy, policyRef, typeInQuery])

  return null
}
